/**
 * System Lizard - Iguanamente DSP Synth Engine (v1.2 Hardware-Exact Specs)
 * Robust Web Audio API engine with isolated patch taps and clean preset state management.
 */

export class SynthEngine {
  constructor() {
    this.ctx = null;
    this.isAudioStarted = false;

    // Base Control Parameters
    this.params = {
      coarseFreq: 220,      // Hz
      fmAttenuvert: 0.0,    // -1.0 to +1.0
      radius: 1.5,          // Aux Osc frequency multiplier
      shapeAttenuvert: 0.5, // 0.0 (Sat Square) -> 0.5 (Sine) -> 1.0 (Wavefold)
      delayFreq: 4.0,       // Delay frequency (Hz)
      feedback: 0.45,       // Delay feedback
      mix: 0.4,             // Equal power mix
      xfm: 0.2,             // XFM depth
      xdt: 0.2,             // XDT depth
      xam: 0.3,             // XAM depth
      masterGain: 0.7
    };

    // Jack CV Modulation Offsets
    this.cvInputs = {
      vo: 0,
      fm: 0,
      rad: 0,
      shp: 0,
      fb: 0,
      freq: 0,
      xfm: 0,
      xdt: 0,
      xam: 0
    };

    // Active Patches Map (cableId -> { patchGain, sourceTap })
    this.activePatches = new Map();

    this.activeNotes = new Set();
    this.basePitchCV = 0;
  }

  init() {
    if (this.ctx) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AudioCtx();

    // 1. Oscillators
    this.mainOsc = this.ctx.createOscillator();
    this.auxOsc = this.ctx.createOscillator();
    this.mainOsc.type = 'sawtooth';
    this.auxOsc.type = 'sine';

    // 2. Wavefolder / WaveShaper
    this.waveShaper = this.ctx.createWaveShaper();
    this.updateWaveShaperCurve(this.params.shapeAttenuvert);

    // 3. FM & AM Cross Modulation
    this.fmGainNode = this.ctx.createGain();
    this.amGainNode = this.ctx.createGain();
    this.amGainNode.gain.value = 1.0;

    this.auxOsc.connect(this.fmGainNode);
    this.fmGainNode.connect(this.mainOsc.frequency);

    // 4. Main & Aux VCAs
    this.mainVCA = this.ctx.createGain();
    this.auxVCA = this.ctx.createGain();
    this.mainVCA.gain.value = 0.6;
    this.auxVCA.gain.value = 0.6;

    this.mainOsc.connect(this.waveShaper);
    this.waveShaper.connect(this.amGainNode);
    this.amGainNode.connect(this.mainVCA);

    this.auxOsc.connect(this.auxVCA);

    // Dedicated Patch Output Taps (Isolated from Master Sound Chain)
    this.mainOutTap = this.ctx.createGain();
    this.auxOutTap = this.ctx.createGain();
    this.mainOutTap.gain.value = 1.0;
    this.auxOutTap.gain.value = 1.0;

    this.mainVCA.connect(this.mainOutTap);
    this.auxVCA.connect(this.auxOutTap);

    // 5. Delay Network
    this.delayNode = this.ctx.createDelay(3.0);
    const initialDelaySec = 1 / Math.max(0.5, this.params.delayFreq);
    this.delayNode.delayTime.value = initialDelaySec;

    this.feedbackGainNode = this.ctx.createGain();
    this.feedbackGainNode.gain.value = this.params.feedback;

    this.delayFilter = this.ctx.createBiquadFilter();
    this.delayFilter.type = 'lowpass';
    this.delayFilter.frequency.value = 3200;

    this.dryGainNode = this.ctx.createGain();
    this.wetGainNode = this.ctx.createGain();
    this.dryGainNode.gain.value = Math.cos(this.params.mix * 0.5 * Math.PI);
    this.wetGainNode.gain.value = Math.sin(this.params.mix * 0.5 * Math.PI);

    this.mainVCA.connect(this.dryGainNode);
    this.mainVCA.connect(this.delayNode);

    this.delayNode.connect(this.delayFilter);
    this.delayFilter.connect(this.feedbackGainNode);
    this.feedbackGainNode.connect(this.delayNode);
    this.delayNode.connect(this.wetGainNode);

    // 6. Master Output & Stereo Split
    this.merger = this.ctx.createChannelMerger(2);
    this.dryGainNode.connect(this.merger, 0, 0);
    this.auxVCA.connect(this.merger, 0, 1);

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.params.masterGain;

    this.dryGainNode.connect(this.masterGain);
    this.wetGainNode.connect(this.masterGain);

    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.8;

    this.masterGain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);

    // Start Oscillators
    this.mainOsc.start();
    this.auxOsc.start();

    this.updateFrequencies();
    this.updateCrossModulation();
  }

  async ensureAudioContext() {
    if (!this.ctx) {
      this.init();
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.isAudioStarted = true;
  }

  updateFrequencies() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;

    const cvTotal = this.basePitchCV + this.cvInputs.vo;
    const pitchMultiplier = Math.pow(2, cvTotal);

    const fmOffset = this.params.fmAttenuvert * 160 + this.cvInputs.fm * 250;
    const mainFreq = Math.max(8, Math.min(8000, (this.params.coarseFreq + fmOffset) * pitchMultiplier));

    const radiusEffective = Math.max(0.1, this.params.radius + this.cvInputs.rad);
    const auxFreq = Math.max(4, Math.min(12000, mainFreq * radiusEffective));

    this.mainOsc.frequency.setTargetAtTime(mainFreq, now, 0.015);
    this.auxOsc.frequency.setTargetAtTime(auxFreq, now, 0.015);

    const fmDepthVal = (this.params.fmAttenuvert * 0.4 + (this.params.xfm + this.cvInputs.xfm) * 1.5) * mainFreq * 0.8;
    this.fmGainNode.gain.setTargetAtTime(Math.max(0, fmDepthVal), now, 0.015);

    const effectiveDelayFreq = Math.max(0.5, this.params.delayFreq + this.cvInputs.freq + (this.params.xdt + this.cvInputs.xdt) * 12);
    const delaySec = Math.max(0.005, Math.min(2.0, 1 / effectiveDelayFreq));
    this.delayNode.delayTime.setTargetAtTime(delaySec, now, 0.02);
  }

  updateCrossModulation() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;

    const xamDepth = Math.max(0, Math.min(1.0, this.params.xam + this.cvInputs.xam));
    const baseAMGain = 1.0 - (xamDepth * 0.7);
    this.amGainNode.gain.setTargetAtTime(baseAMGain, now, 0.02);
  }

  updateWaveShaperCurve(shapeVal) {
    const n = 1024;
    const curve = new Float32Array(n);
    const shpCV = this.cvInputs.shp;
    const effectiveShape = Math.max(0, Math.min(1, shapeVal + shpCV));

    for (let i = 0; i < n; i++) {
      const x = (i * 2) / n - 1;
      if (effectiveShape < 0.4) {
        const drive = 1 + (0.4 - effectiveShape) * 20;
        curve[i] = Math.tanh(x * drive);
      } else if (effectiveShape < 0.6) {
        curve[i] = Math.sin(x * Math.PI * 0.5);
      } else {
        const fold = 1 + (effectiveShape - 0.6) * 10;
        curve[i] = Math.sin(x * fold * Math.PI * 0.8);
      }
    }
    if (this.waveShaper) {
      this.waveShaper.curve = curve;
    }
  }

  connectPatch(cableId, fromJackId, toJackId, lfoEngine = null) {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;

    // Disconnect existing cable if re-plugging same slot
    this.disconnectPatch(cableId);

    let sourceTap = null;
    if (fromJackId === 'jack-mainout') sourceTap = this.mainOutTap;
    else if (fromJackId === 'jack-auxout') sourceTap = this.auxOutTap;
    else if (fromJackId === 'jack-lfoout' && lfoEngine) sourceTap = lfoEngine.gain;

    const destKey = toJackId.replace('jack-', '');
    this.cvInputs[destKey] = 0.5; // Apply positive CV modulation offset

    if (sourceTap) {
      const patchGain = this.ctx.createGain();
      patchGain.gain.setValueAtTime(0.3, now);
      sourceTap.connect(patchGain);

      // Route to destination
      if (destKey === 'vo') patchGain.connect(this.mainOsc.frequency);
      else if (destKey === 'fm') patchGain.connect(this.mainOsc.frequency);
      else if (destKey === 'rad') patchGain.connect(this.auxOsc.frequency);
      else if (destKey === 'fb') patchGain.connect(this.feedbackGainNode.gain);
      else if (destKey === 'freq') patchGain.connect(this.delayNode.delayTime);

      this.activePatches.set(cableId, { patchGain, sourceTap, destKey });
    }

    this.updateFrequencies();
    this.updateCrossModulation();
    this.updateWaveShaperCurve(this.params.shapeAttenuvert);
  }

  disconnectPatch(cableId) {
    const patch = this.activePatches.get(cableId);
    if (patch) {
      try {
        patch.sourceTap.disconnect(patch.patchGain);
        patch.patchGain.disconnect();
      } catch (e) {
        // Silently catch clean disconnects
      }
      this.cvInputs[patch.destKey] = 0;
      this.activePatches.delete(cableId);
    }

    this.updateFrequencies();
    this.updateCrossModulation();
    this.updateWaveShaperCurve(this.params.shapeAttenuvert);
  }

  clearAllPatches() {
    for (const cableId of Array.from(this.activePatches.keys())) {
      this.disconnectPatch(cableId);
    }
    for (const key of Object.keys(this.cvInputs)) {
      this.cvInputs[key] = 0;
    }
    this.updateFrequencies();
    this.updateCrossModulation();
    this.updateWaveShaperCurve(this.params.shapeAttenuvert);
  }

  setParam(key, value) {
    this.params[key] = value;
    if (!this.ctx) return;
    const now = this.ctx.currentTime;

    switch (key) {
      case 'coarseFreq':
      case 'fmAttenuvert':
      case 'radius':
      case 'xfm':
      case 'delayFreq':
      case 'xdt':
        this.updateFrequencies();
        break;
      case 'xam':
        this.updateCrossModulation();
        break;
      case 'shapeAttenuvert':
        this.updateWaveShaperCurve(value);
        break;
      case 'feedback':
        const fbCV = this.cvInputs.fb;
        const totalFB = Math.max(0, Math.min(0.95, value + fbCV * 0.5));
        this.feedbackGainNode.gain.setTargetAtTime(totalFB, now, 0.02);
        break;
      case 'mix':
        const dryGain = Math.cos(value * 0.5 * Math.PI);
        const wetGain = Math.sin(value * 0.5 * Math.PI);
        this.dryGainNode.gain.setTargetAtTime(dryGain, now, 0.02);
        this.wetGainNode.gain.setTargetAtTime(wetGain, now, 0.02);
        break;
      case 'masterGain':
        this.masterGain.gain.setTargetAtTime(value, now, 0.02);
        break;
    }
  }

  noteOn(midiNote, velocity = 1.0) {
    this.ensureAudioContext();
    this.activeNotes.add(midiNote);

    const semitones = midiNote - 60;
    this.basePitchCV = semitones / 12;

    this.updateFrequencies();
    this.updateCrossModulation();

    const now = this.ctx.currentTime;
    const gainVal = Math.min(1.0, velocity * 0.85);
    this.mainVCA.gain.cancelScheduledValues(now);
    this.mainVCA.gain.setValueAtTime(this.mainVCA.gain.value, now);
    this.mainVCA.gain.linearRampToValueAtTime(gainVal, now + 0.015);
  }

  noteOff(midiNote) {
    this.activeNotes.delete(midiNote);
    if (this.activeNotes.size === 0 && this.ctx) {
      const now = this.ctx.currentTime;
      this.mainVCA.gain.cancelScheduledValues(now);
      this.mainVCA.gain.setValueAtTime(this.mainVCA.gain.value, now);
      this.mainVCA.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    } else if (this.activeNotes.size > 0) {
      const lastNote = Array.from(this.activeNotes).pop();
      this.noteOn(lastNote);
    }
  }

  getWaveformData(buffer) {
    if (this.analyser) {
      this.analyser.getFloatTimeDomainData(buffer);
    }
  }
}
